/**
 * WORK STUDIES.
 *
 * These are presented as one set of examples. The grid carries no badge and no
 * live/concept split, because the point of the page is the work.
 *
 *   kind: 'live'    — a real commissioned site. Named, and linked to the
 *                     running site from its own page. Only ever added with the
 *                     client's consent.
 *   kind: 'concept' — a self-initiated study for an invented company. The brand
 *                     does not exist and the imagery is generated.
 *
 * Every study has a `liveUrl`, so every example can be opened and walked
 * through rather than looked at as a picture. For the commissioned sites that
 * is the client's own domain. For the concepts it is a full build of the
 * invented brand, deployed to its own project and served noindex, because a
 * company that does not exist should not turn up in search looking like one.
 *
 * `kind` no longer drives anything visible. It survives so the build-time SEO
 * plugin can mark a concept as self-initiated in structured data, which stops
 * an assistant asserting an invented company was a client. Removing the badges
 * was a presentation decision, not licence to imply otherwise.
 *
 * We do not publish redesigns produced for prospects who have not commissioned
 * them. Those are walked through privately.
 *
 * Copy rule: plain English. No jargon, no acronyms, no product-speak.
 * Imagery provenance is recorded in `public/media/PROVENANCE.md`.
 */

export const workStudies = [
  {
    slug: 'rose-skin-academy',
    kind: 'live',
    title: 'Rose Skin Academy',
    sector: 'Training academy',
    liveUrl: 'https://www.roseskinacademy.com/',
    image: '/media/work/live/rose-skin-academy-homepage.webp',
    alt: 'The Rose Skin Academy homepage',
    mobileImage: '/media/work/live/rose-skin-academy-mobile.webp',
    mobileAlt: 'The Rose Skin Academy homepage on a phone',
    summary: 'A long list of courses, turned into one clear path per person.',
    problem:
      'The academy taught a lot of things. Someone landing on the site could not tell which course was theirs, what order to take them in, or what they would be able to do at the end.',
    change:
      'We built the site around the person, not the catalogue. You pick who you are, you get your route, and applying is right there.',
    search:
      'Every course and every question now has a page that answers it properly, so Google and ChatGPT have something real to work with.',
  },
  {
    slug: 'anecdotal-health',
    kind: 'live',
    title: 'Anecdotal Health',
    sector: 'Health brand',
    liveUrl: 'https://anecdotal.health/',
    image: '/media/work/live/anecdotal-health-homepage.webp',
    alt: 'The Anecdotal Health homepage',
    mobileImage: '/media/work/live/anecdotal-health-mobile.webp',
    mobileAlt: 'The Anecdotal Health homepage on a phone',
    summary: 'A crowded, low-trust category, made easy to make a decision in.',
    problem:
      'Everyone in the space makes the same promises. The site had to show real research behind it without making a health decision feel cold or overwhelming.',
    change:
      'One simple first step, then filters and short lists behind it. The point of view is stated plainly instead of hidden in a wall of studies.',
    search:
      'Each subject has its own page saying what is actually known, which is exactly what people type into Google and ask ChatGPT.',
  },
  {
    slug: 'bizday',
    kind: 'live',
    title: 'BizDay',
    sector: 'Software',
    liveUrl: 'https://bizday.dev/',
    image: '/media/work/live/bizday-homepage.webp',
    alt: 'The BizDay homepage',
    mobileImage: '/media/work/live/bizday-mobile.webp',
    mobileAlt: 'The BizDay homepage on a phone',
    summary: 'A technical product explained without dumbing it down.',
    problem:
      'Two very different people land on the same page: a developer who wants to try it, and a buyer who wants to know what it is for. Neither was getting what they needed.',
    change:
      'The promise first, a way to try it second, and the technical detail one click away rather than hidden three levels down.',
    search:
      'What it does is written in normal words, so a search result or an answer can describe it correctly.',
  },
  {
    slug: 'velora-yachts',
    kind: 'concept',
    liveUrl: 'https://velora-yachts-theta.vercel.app',
    title: 'Velora Yachts',
    sector: 'Yacht charter',
    image: '/media/work/originals/velora-yachts-homepage.webp',
    alt: 'A yacht charter homepage concept',
    summary: 'Selling a boat, when the customer is buying a week of their life.',
    problem:
      'Charter sites lead with length, cabins and crew numbers. None of that tells a first-time charterer whether this is the right week for their family.',
    change:
      'The week comes first: where you go, what the days feel like, who it suits. The specification is still there, just not the opening line.',
    search:
      'Real pages for real questions, like where to sail in June with children, instead of one page listing every boat.',
  },
  {
    slug: 'aurelis-aviation',
    kind: 'concept',
    liveUrl: 'https://aurelis-aviation.vercel.app',
    title: 'Aurelis Aviation',
    sector: 'Private aviation',
    image: '/media/work/originals/aurelis-aviation-homepage.webp',
    alt: 'A private aviation homepage concept',
    summary: 'Everyone offers the same aircraft. So the fleet list is not the pitch.',
    problem:
      'Charter brokers all access the same planes from the same operators. A page of aircraft says nothing about why you would call this one.',
    change:
      'The site is about how they work and what they take off your hands, and asking for a quote is one short step.',
    search:
      'A page for each route and each aircraft type, written as an answer, so they can be named when someone asks who to fly with.',
  },
  {
    slug: 'wild-origin-safaris',
    kind: 'concept',
    liveUrl: 'https://wild-origin-safaris.vercel.app',
    title: 'Wild Origin Safaris',
    sector: 'Safari',
    image: '/media/work/originals/wild-origin-safaris-homepage.webp',
    alt: 'A safari operator homepage concept',
    summary: 'The value is knowing where to send you. The site was showing a list of camps.',
    problem:
      'Which camp, which month, for which traveller, is the whole skill. It was nowhere on the site.',
    change:
      'Organised by who is going and when, not by property. The judgement is the product, so it goes on the page.',
    search:
      'The questions people genuinely ask first, like when to go and where to start, answered in full.',
  },
  {
    slug: 'ashcombe-homes',
    kind: 'concept',
    liveUrl: 'https://ashcombe-homes.vercel.app',
    title: 'Ashcombe Homes',
    sector: 'Homebuilder',
    image: '/media/work/originals/ashcombe-homepage-v2.webp',
    alt: 'A homebuilder homepage concept',
    summary: 'The houses had outgrown the website.',
    problem:
      'Strong developments, let down by near-identical plot pages, brochure downloads and old photography. Expensive homes made to look ordinary.',
    change:
      'Each development gets its story told properly, with better photography and one simple way to ask about it.',
    search:
      'Pages about the places people are actually choosing between, not just the plots for sale this month.',
  },
];

export const liveWork = workStudies.filter((item) => item.kind === 'live');
export const conceptWork = workStudies.filter((item) => item.kind === 'concept');

export const featuredWork = [...liveWork, ...conceptWork].slice(0, 6);

export const findWorkStudy = (slug) => workStudies.find((item) => item.slug === slug);
